"use client"

import { useFormik, FormikProvider } from "formik"
import { Loader2 } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { FormInput, FormSelect } from "@/components/form-inputs"
import {
  useCreateAdminPartnerCodeMutation,
  useSetAdminCodeCommissionMutation,
} from "@/hooks/mutations/partners"
import { getBONUS_LIMIT_OPTIONS, bonusLimitCountError, bonusLimitFrom, bonusPercentError, parseBonusPercent, parsePercentInput, type BonusLimitMode, type PartnerBonusTranslator } from "@/components/partners/client-bonus"
import { formatPercent } from "@/lib/partners/percent"
import type { AdminPartnerCodeView, AdminPartnerView } from "@/server-actions/admin-partners"
import { useTranslations } from "next-intl"




type CreateCodeForm = {
  label: string
  commissionPercent: string
  bonusPercent: string
  limitMode: BonusLimitMode
  limitCount: string
}

export function CreateCodeForPartnerDialog({
  open,
  partner,
  onOpenChange,
}: {
  open: boolean
  partner: AdminPartnerView
  onOpenChange: (open: boolean) => void
}) {
  const tBonus = useTranslations("PartnerBonus")
  const BONUS_LIMIT_OPTIONS = getBONUS_LIMIT_OPTIONS(useTranslations("Metadata.PartnersClientBonus"))



  const tUi = useTranslations("Partners.PartnersAdminAdminCodeDialogs")

  const mutation = useCreateAdminPartnerCodeMutation()

  const formik = useFormik<CreateCodeForm>({
    initialValues: { label: "", commissionPercent: "", bonusPercent: "0", limitMode: "always", limitCount: "2" },
    validate: values => validateCreate(values, partner.commission_percent, tBonus),
    onSubmit: async values => {
      const commission = parsePercentInput(values.commissionPercent)
      if (commission === "invalid") return

      try {
        await mutation.mutateAsync({
          partner_id: partner.partner_id,
          label: values.label.trim() || null,
          commission_percent: commission,
          client_bonus_percent: parseBonusPercent(values.bonusPercent),
          client_bonus_payment_limit: bonusLimitFrom(values.limitMode, values.limitCount),
        })
        formik.resetForm()
        onOpenChange(false)
      } catch {
        // withQueryHandling already surfaced the error.
      }
    },
  })

  return (
    <Dialog open={open} onOpenChange={next => !mutation.isPending && onOpenChange(next)}>
      <DialogContent>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <DialogHeader>
              <DialogTitle><span key={tUi("newReferralLink")}>{tUi("newReferralLink")}</span></DialogTitle>
              <DialogDescription>
                {<span key={tUi("createdOnBehalfOfValue0ThePartnerSees", { value0: String(partner.tenant_name) })}>{tUi("createdOnBehalfOfValue0ThePartnerSees", { value0: String(partner.tenant_name) })}</span>}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-5">
              <FormInput name="label" label={tUi("name")} placeholder={tUi("conferenceCampaign")} maxLength={80} autoFocus />
              <FormInput
                name="commissionPercent"
                label={tUi("commission")}
                inputMode="decimal"
                placeholder={tUi("partnerSValue0", { value0: String(formatPercent(partner.commission_percent)) })}
                description={tUi("leaveEmptyToFollowThePartnerSOwn")}
              />
              <FormInput name="bonusPercent" label={tUi("clientBonus")} inputMode="decimal" />
              <FormSelect<BonusLimitMode> name="limitMode" label={tUi("bonusAppliesTo")} options={BONUS_LIMIT_OPTIONS} />
              {formik.values.limitMode === "limited" && (
                <FormInput name="limitCount" label={tUi("numberOfPayments")} inputMode="numeric" />
              )}
            </div>

            <DialogFooter>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                <span key={tUi("createLink")}>{tUi("createLink")}</span></Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}

export function SetCodeCommissionDialog({
  code,
  partnerPercent,
  onOpenChange,
}: {
  /** Null when no link is being edited. */
  code: AdminPartnerCodeView | null
  partnerPercent: number
  onOpenChange: (open: boolean) => void
}) {


  const tUi = useTranslations("Partners.PartnersAdminAdminCodeDialogs")

  const tBonus = useTranslations("PartnerBonus")
  const mutation = useSetAdminCodeCommissionMutation()

  const formik = useFormik<{ commissionPercent: string }>({
    enableReinitialize: true,
    initialValues: {
      commissionPercent: code?.commission_percent_override === null || code === null
        ? ""
        : String(code.commission_percent_override),
    },
    validate: values => {
      const percent = parsePercentInput(values.commissionPercent)
      return percent === "invalid"
        ? { commissionPercent: tBonus("commission") }
        : {}
    },
    onSubmit: async values => {
      const percent = parsePercentInput(values.commissionPercent)
      if (percent === "invalid" || !code) return

      try {
        await mutation.mutateAsync({ code_id: code.id, commission_percent: percent })
        onOpenChange(false)
      } catch {
        // withQueryHandling already surfaced the error.
      }
    },
  })

  const nextPercent = parsePercentInput(formik.values.commissionPercent)
  const effectivePercent = nextPercent === "invalid" || nextPercent === null ? partnerPercent : nextPercent
  const clampsBonus = Boolean(code && code.client_bonus_percent > effectivePercent)

  return (
    <Dialog open={Boolean(code)} onOpenChange={next => !mutation.isPending && onOpenChange(next)}>
      <DialogContent>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <DialogHeader>
              <DialogTitle><span key={tUi("linkCommission")}>{tUi("linkCommission")}</span></DialogTitle>
              <DialogDescription>
                <span key={code ? tUi("value0Value1", { value0: String(code.label ?? "Referral link"), value1: String(code.code) }) : null}>{code ? tUi("value0Value1", { value0: String(code.label ?? "Referral link"), value1: String(code.code) }) : null}</span>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-5">
              <FormInput
                name="commissionPercent"
                label={tUi("commission")}
                inputMode="decimal"
                placeholder={tUi("partnerSValue0", { value0: String(formatPercent(partnerPercent)) })}
                description={tUi("leaveEmptyToInheritThePartnerSPercent")}
                autoFocus
              />
              {clampsBonus && code && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {<span key={tUi("thisLinkPromisesTheClientValue0AtValue1", { value0: String(formatPercent(code.client_bonus_percent)), value1: String(formatPercent(effectivePercent)) })}>{tUi("thisLinkPromisesTheClientValue0AtValue1", { value0: String(formatPercent(code.client_bonus_percent)), value1: String(formatPercent(effectivePercent)) })}</span>}
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                <span key={tUi("save")}>{tUi("save")}</span></Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}

function validateCreate(values: CreateCodeForm, partnerPercent: number, tBonus: PartnerBonusTranslator) {
  const errors: Partial<Record<keyof CreateCodeForm, string>> = {}
  const commission = parsePercentInput(values.commissionPercent)
  if (commission === "invalid") {
    errors.commissionPercent = tBonus("commission")
  }

  const ceiling = commission === "invalid" || commission === null ? partnerPercent : commission
  const bonusError = bonusPercentError(
    values.bonusPercent,
    ceiling,
    tBonus("linkCeiling", {percent: formatPercent(ceiling)}),
    tBonus,
  )
  if (bonusError) errors.bonusPercent = bonusError

  if (values.limitMode === "limited") {
    const limitError = bonusLimitCountError(values.limitCount, tBonus)
    if (limitError) errors.limitCount = limitError
  }

  return errors
}
